import React, { useState } from "react";
import { caApi } from "../../../../../backend_urls/caApi";
import ClientSearchDropdown from "../../../models/ClientSearchDropdown";
import AdvanceReminderOptions from "./AdvanceReminderOptions";
import BaseIcon from "../../../../../components/svg_icons/BaseIcon";
import "../../../../../css/pages/ca_pages/CANotificationsPage.css";

/*
 * -------------------------------------------------------------
 * INITIAL FORM
 * -------------------------------------------------------------
 */

const getInitialFormData = () => ({
  title: "",
  clientId: "",
  documentType: "",
  dueDate: "",
  description: "",

  /*
   * Reminder builder state.
   *
   * AdvanceReminderOptions owns the UI structure.
   * This component only converts it into the Django API format.
   */
  reminders: [],

  /*
   * Global/default reminder timezone.
   */
  timezone: "Asia/Kolkata",

  /*
   * Request expiry.
   */
  requestExpiryDays: 30,
});

/*
 * -------------------------------------------------------------
 * DATE HELPERS
 * -------------------------------------------------------------
 */

/*
 * The backend stores the timezone separately from scheduled_for.
 *
 * Therefore we intentionally do NOT convert the selected local
 * CA time into UTC here.
 *
 * Example:
 *
 * 2026-09-30 + 09:00
 *
 * becomes:
 *
 * 2026-09-30T09:00:00
 *
 * with:
 *
 * timezone = Asia/Kolkata
 */
const buildScheduledDateTime = (date, time) => {
  if (!date) {
    return "";
  }

  const safeTime = time || "09:00";

  return `${date}T${safeTime}:00`;
};

/*
 * Django/DRF accepts the datetime-local representation when
 * seconds are included.
 */
const buildDueDate = (value) => {
  if (!value) {
    return "";
  }

  if (value.length === 16) {
    return `${value}:00`;
  }

  return value;
};

/*
 * -------------------------------------------------------------
 * API ERROR HANDLING
 * -------------------------------------------------------------
 */

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return "Unable to create document request.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (typeof data.detail === "string") {
    return data.detail;
  }

  if (typeof data === "object") {
    const messages = [];

    Object.entries(data).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        value.forEach((message) => {
          messages.push(`${field}: ${message}`);
        });
      } else if (typeof value === "string") {
        messages.push(`${field}: ${value}`);
      }
    });

    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  return "Unable to create document request.";
};

/*
 * -------------------------------------------------------------
 * DELIVERY NORMALIZATION
 * -------------------------------------------------------------
 *
 * Backend expects:
 *
 * deliveries: [
 *     {
 *         platform,
 *         template_key,
 *         custom_subject,
 *         custom_message,
 *         template_data,
 *         is_enabled
 *     }
 * ]
 */
const normalizeDelivery = (delivery) => ({
  platform: delivery?.platform || "",

  template_key: delivery?.template_key || "",

  custom_subject: delivery?.custom_subject || "",

  custom_message: delivery?.custom_message || "",

  template_data:
    delivery?.template_data && typeof delivery.template_data === "object"
      ? delivery.template_data
      : {},

  is_enabled: delivery?.is_enabled !== false,
});

/*
 * -------------------------------------------------------------
 * REMINDER NORMALIZATION
 * -------------------------------------------------------------
 *
 * Backend contract:
 *
 * {
 *     reminder_number: 1,
 *     scheduled_for: "...",
 *     timezone: "...",
 *     deliveries: [...]
 * }
 */
const buildReminderPayload = (reminder, index, defaultTimezone) => {
  const timezone = reminder?.timezone || defaultTimezone || "Asia/Kolkata";

  /*
   * New reminder builder should provide deliveries.
   */
  let deliveries = Array.isArray(reminder?.deliveries)
    ? reminder.deliveries
    : [];

  /*
   * Backward compatibility with the previous
   * platforms-based reminder structure.
   */
  if (deliveries.length === 0 && Array.isArray(reminder?.platforms)) {
    deliveries = reminder.platforms.map((platform) => ({
      platform,

      template_key:
        platform === "whatsapp"
          ? "document_request_reminder"
          : platform === "email"
            ? "document_request"
            : "",

      custom_subject: "",

      custom_message:
        reminder?.messageMode === "custom" ? reminder?.message || "" : "",

      template_data: {},

      is_enabled: true,
    }));
  }

  return {
    /*
     * Django validates uniqueness of reminder_number.
     */
    reminder_number: Number(reminder?.reminderNumber) || index + 1,

    scheduled_for: buildScheduledDateTime(reminder?.date, reminder?.time),

    timezone,

    deliveries: deliveries.map(normalizeDelivery),
  };
};

/*
 * -------------------------------------------------------------
 * COMPONENT
 * -------------------------------------------------------------
 */

const CreateDocumentRequestModal = ({ onClose, onSave }) => {
  const [selectedClient, setSelectedClient] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  const [formData, setFormData] = useState(getInitialFormData);

  /*
   * ---------------------------------------------------------
   * BASIC FORM CHANGES
   * ---------------------------------------------------------
   */

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  /*
   * ---------------------------------------------------------
   * CLIENT SELECTION
   * ---------------------------------------------------------
   */

  const handleClientSelect = (client) => {
    setSelectedClient(client);

    setFormData((previous) => ({
      ...previous,

      clientId: client?.id || "",

      clientName: client?.user_full_name || client?.name || "",
    }));

    setError("");
  };

  /*
   * ---------------------------------------------------------
   * BUILD API PAYLOAD
   * ---------------------------------------------------------
   */

  const buildPayload = () => {
    const reminders = Array.isArray(formData.reminders)
      ? formData.reminders
      : [];

    const payload = {
      title: formData.title.trim(),

      document_type: formData.documentType.trim(),

      due_date: buildDueDate(formData.dueDate),

      description: formData.description.trim(),

      /*
       * This field exists in the backend model according
       * to the API response/error you showed earlier.
       */
      request_expiry_days: Number(formData.requestExpiryDays) || 30,

      /*
       * This is the important backend contract.
       */
      reminders: reminders.map((reminder, index) =>
        buildReminderPayload(reminder, index, formData.timezone),
      ),
    };

    return payload;
  };

  /*
   * ---------------------------------------------------------
   * VALIDATION
   * ---------------------------------------------------------
   */

  const validateForm = () => {
    if (!formData.clientId) {
      return "Please select a client.";
    }

    if (!formData.title.trim()) {
      return "Please enter a request title.";
    }

    if (!formData.documentType.trim()) {
      return "Please enter the document type.";
    }

    if (!formData.dueDate) {
      return "Please select a submission deadline.";
    }

    const expiryDays = Number(formData.requestExpiryDays);

    if (!Number.isFinite(expiryDays) || expiryDays < 1) {
      return "Request expiry must be at least 1 day.";
    }

    const reminders = Array.isArray(formData.reminders)
      ? formData.reminders
      : [];

    /*
     * Validate every reminder before
     * sending it to Django.
     */
    for (let index = 0; index < reminders.length; index += 1) {
      const reminder = reminders[index];

      if (!reminder?.date) {
        return `Please select a date for Reminder ${index + 1}.`;
      }

      if (!reminder?.time) {
        return `Please select a time for Reminder ${index + 1}.`;
      }

      const deliveries = Array.isArray(reminder?.deliveries)
        ? reminder.deliveries
        : Array.isArray(reminder?.platforms)
          ? reminder.platforms.map((platform) => ({
              platform,
            }))
          : [];

      if (deliveries.length === 0) {
        return `Reminder ${index + 1} must have at least one delivery channel.`;
      }

      const enabledDeliveries = deliveries.filter(
        (delivery) => delivery?.is_enabled !== false,
      );

      if (enabledDeliveries.length === 0) {
        return `Reminder ${
          index + 1
        } must have at least one enabled delivery channel.`;
      }

      /*
       * Every delivery must have a platform.
       */
      const invalidDelivery = deliveries.find(
        (delivery) => !delivery?.platform,
      );

      if (invalidDelivery) {
        return `Reminder ${index + 1} contains a delivery without a platform.`;
      }
    }

    return "";
  };

  /*
   * ---------------------------------------------------------
   * SUBMIT
   * ---------------------------------------------------------
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!formData.clientId) {
      setError("Please select a client.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const payload = buildPayload();

      console.log("Creating document request:", payload);

      await caApi.createClientDocumentRequest(formData.clientId, payload);

      /*
       * Refresh parent list.
       */
      if (onSave) {
        await onSave();
      }

      /*
       * Reset form.
       */
      setSelectedClient(null);
      setFormData(getInitialFormData());

      onClose();
    } catch (requestError) {
      console.error("Error creating document request:", requestError);

      setError(getApiErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <div className="document-request-modal">
      <div className="document-request-modal__overlay">
        <div className="document-request-modal__container">
          {/* =================================================
                        HEADER
                    ================================================= */}

          <div className="document-request-modal__header">
            <div className="document-request-modal__header-content">
              <div className="document-request-modal__icon">
                <span>
                  <BaseIcon name="document" />
                </span>
              </div>

              <div>
                <h2>Create Document Request</h2>

                <p>
                  Request documents from your client and configure automated
                  reminders exactly the way you need.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="document-request-modal__close"
              onClick={onClose}
              aria-label="Close"
              disabled={submitting}
            >
              ×
            </button>
          </div>

          {/* =================================================
                        ERROR
                    ================================================= */}

          {error && (
            <div className="document-request-error" role="alert">
              {error}
            </div>
          )}

          {/* =================================================
                        FORM
                    ================================================= */}

          <form
            className="document-request-form"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* =================================================
                            SECTION 01 — REQUEST
                        ================================================= */}

            <section className="document-request-section">
              <div className="document-request-section__header">
                <div className="document-request-section__number">01</div>

                <div>
                  <h3>Request details</h3>

                  <p>
                    Define what you need from the client and when it is due.
                  </p>
                </div>
              </div>

              <div className="document-request-fields">
                {/* TITLE */}

                <div className="document-request-field document-request-field--full">
                  <label htmlFor="request-title">Request title</label>

                  <span className="document-request-field__hint">
                    Give the request a clear name your client will understand.
                  </span>

                  <input
                    id="request-title"
                    type="text"
                    name="title"
                    placeholder="e.g. Documents required for ITR filing"
                    value={formData.title}
                    onChange={handleChange}
                    maxLength={255}
                    required
                  />
                </div>

                {/* CLIENT */}

                <div className="document-request-field document-request-field--full">
                  <label>Client</label>

                  <span className="document-request-field__hint">
                    Select the client who should receive this request.
                  </span>

                  <ClientSearchDropdown onSelect={handleClientSelect} />

                  {selectedClient && (
                    <div className="selected-client">
                      <span className="selected-client__icon">✓</span>

                      <span>
                        Selected client:{" "}
                        <strong>
                          {selectedClient.user_full_name ||
                            selectedClient.name ||
                            selectedClient.email}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* DOCUMENT TYPE */}

                <div className="document-request-field">
                  <label htmlFor="document-type">Document type</label>

                  <span className="document-request-field__hint">
                    Specify what the client needs to provide.
                  </span>

                  <input
                    id="document-type"
                    type="text"
                    name="documentType"
                    placeholder="e.g. Bank statement"
                    value={formData.documentType}
                    onChange={handleChange}
                    maxLength={100}
                    required
                  />
                </div>

                {/* DUE DATE */}

                <div className="document-request-field">
                  <label htmlFor="due-date">Submission deadline</label>

                  <span className="document-request-field__hint">
                    When the requested document should be submitted.
                  </span>

                  <input
                    id="due-date"
                    type="datetime-local"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* DESCRIPTION */}

                <div className="document-request-field document-request-field--full">
                  <label htmlFor="request-description">
                    Instructions for client
                  </label>

                  <span className="document-request-field__hint">
                    Add any instructions, context or requirements.
                  </span>

                  <textarea
                    id="request-description"
                    name="description"
                    placeholder="Explain what the client needs to upload or provide..."
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                {/* EXPIRY */}

                <div className="document-request-field">
                  <label htmlFor="request-expiry-days">Request expiry</label>

                  <span className="document-request-field__hint">
                    Number of days the request should remain active.
                  </span>

                  <input
                    id="request-expiry-days"
                    type="number"
                    name="requestExpiryDays"
                    min="1"
                    max="3650"
                    value={formData.requestExpiryDays}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* =================================================
                            SECTION 02 — REMINDERS
                        ================================================= */}

            <section className="document-request-section document-request-section--automation">
              <div className="document-request-section__header">
                <div className="document-request-section__number">02</div>

                <div>
                  <h3>Reminder automation</h3>

                  <p>
                    Build your own reminder sequence and choose exactly how each
                    reminder should be delivered.
                  </p>
                </div>
              </div>

              <AdvanceReminderOptions
                values={formData}
                setValues={setFormData}
              />
            </section>

            {/* =================================================
                            FOOTER
                        ================================================= */}

            <div className="document-request-form__footer">
              <div className="document-request-form__footer-note">
                <span>🔒</span>

                <span>
                  The request and its reminder configuration will be securely
                  associated with the selected client.
                </span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="primary-btn"
                  disabled={submitting || !formData.clientId}
                >
                  {submitting ? "Creating Request..." : "Send Document Request"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDocumentRequestModal;
